import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Card, CardContent,
    CardActionArea, CircularProgress, Stack, Button
} from '@mui/material';
import LocationOnIcon        from '@mui/icons-material/LocationOn';
import EmailIcon             from '@mui/icons-material/Email';
import AddIcon               from '@mui/icons-material/Add';
import { useNavigate }       from 'react-router-dom';
import { useTranslation }    from 'react-i18next';
import axios                 from '../api/axiosConfig';
import { useAuth }           from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';

const typeColor = (type) => {
    const map = {
        CITY:      { bgcolor: FOREST,     color: 'white' },
        METROPOLIS:{ bgcolor: WALNUT,     color: 'white' },
        MEGAPOLIS: { bgcolor: '#5C1A1A',  color: 'white' },
        TOWN:      { bgcolor: GOLD,       color: WALNUT  },
        VILLAGE:   { bgcolor: TERRACOTTA, color: 'white' },
    };
    return map[type] || { bgcolor: '#999', color: 'white' };
};

export default function DistrictList() {
    const { user }   = useAuth();
    const navigate   = useNavigate();
    const { t }      = useTranslation();

    const [districts, setDistricts] = useState([]);
    const [loading,   setLoading]   = useState(true);

    const isAdmin   = user?.role === 'ADMIN';
    const isManager = user?.role === 'MANAGER';

    // Guard
    useEffect(() => {
        if (user && !isAdmin && !isManager) navigate('/');
    }, [user, isAdmin, isManager, navigate]);

    useEffect(() => {
        axios.get('/districts/view?size=1000')
            .then(res => setDistricts(Array.isArray(res.data) ? res.data : (res.data?.content || [])))
            .catch(() => setDistricts([]))
            .finally(() => setLoading(false));
    }, []);

    if (!user || (!isAdmin && !isManager)) return null;

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://upload.wikimedia.org/wikipedia/commons/d/d5/Yerevan_Armenia_with_the_backdrop_of_Mount_Ararat.JPG") center/cover fixed` }}>
            {/* Banner */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${TERRACOTTA} 100%)`,
                height: 120, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '40px', background: PARCHMENT, borderRadius: '40px 40px 0 0' },
            }} />

            <Container maxWidth="lg" sx={{ mt: -4, pb: 8, position: 'relative', zIndex: 1 }}>
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="overline"
                            sx={{ color: FOREST, letterSpacing: '0.2em', fontWeight: 700 }}>
                            {t('districts.overline', 'Districts')}
                        </Typography>
                        <Typography variant="h4" fontWeight={900}
                            sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {isAdmin
                                ? t('districts.title_admin', 'All Districts')
                                : t('districts.title_manager', 'My District')}
                        </Typography>
                    </Box>
                    {isAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            component="a" href="/districts/create"
                            sx={{
                                bgcolor: TERRACOTTA, color: 'white', borderRadius: 2, fontWeight: 700,
                                boxShadow: '0 4px 12px rgba(196,98,45,0.3)',
                                '&:hover': { bgcolor: '#9A4A1F' },
                                alignSelf: 'center',
                            }}>
                            {t('districts.create_btn', 'Create District')}
                        </Button>
                    )}
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={8}>
                        <CircularProgress sx={{ color: TERRACOTTA }} />
                    </Box>
                ) : districts.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>
                        {t('districts.none', 'No districts found.')}
                    </Typography>
                ) : (
                    <Grid container spacing={3}>
                        {districts.map(d => {
                            const tc = typeColor(d.type);
                            return (
                                <Grid item xs={12} key={d.id}>
                                    <Card elevation={0} sx={{
                                        border: '1px solid rgba(196,98,45,0.12)',
                                        borderRadius: 3,
                                        background: `linear-gradient(135deg, #FFFFFF 0%, ${PARCHMENT} 100%)`,
                                        transition: 'box-shadow 0.2s',
                                        height: '100%',
                                        '&:hover': { boxShadow: '0 8px 28px rgba(196,98,45,0.13)' },
                                    }}>
                                        <CardActionArea component="a" href={`/districts/${d.id}`}>
                                            <CardContent sx={{ p: 3 }}>
                                                {/* Name (primary title) */}
                                                <Typography variant="h6" fontWeight={800}
                                                    sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif", mb: 0.5 }}>
                                                    {d.name}
                                                </Typography>

                                                {/* (TYPE) Region */}
                                                <Typography variant="caption"
                                                    sx={{ color: tc.bgcolor, fontWeight: 700, display: 'block', mb: 1 }}>
                                                    ({d.type}) {d.region}
                                                </Typography>

                                                <Stack spacing={0.8} mt={0.5}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <LocationOnIcon sx={{ fontSize: '0.95rem', color: TERRACOTTA }} />
                                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                            {d.address}
                                                        </Typography>
                                                    </Stack>
                                                    {d.email && (
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <EmailIcon sx={{ fontSize: '0.95rem', color: TERRACOTTA }} />
                                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                {d.email}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Container>
        </Box>
    );
}
